import { Controller } from '@hotwired/stimulus';
import fitAutoresizeTextareas from 'helpers/autoresize';

// "Customize Goals Shown" for the Goals v3 goal components (the Plan of Care and evaluation goals
// editor, the discharge goals, a visit note's session goals), on the fill page and the show page.
//
// The checklist is built each time the picker opens, from the goal rows on the page (row targets),
// so a goal added since the page loaded is listed too. A row with no goal id (a goal written on this
// form and not saved yet) is not listed and always shows, and a row its own component keeps hidden
// (a visit note goal not added to the session) is not listed. A goal left unchecked hides on the
// page at once, a long-term goal unchecked takes its short-term goals with it, and a short-term goal
// checked brings its long-term goal back: a short-term goal cannot stand without it.
//
// The choice is { active, shown, offered }: whether a choice was made, the goals it shows, and the
// goals it was made over, so a goal added after the choice (never offered) shows rather than hides.
//
// Three rows show whatever the choice says, each with its long-term goal: a row a save flagged
// (data-gv3-flagged="true": a decision a closing long-term goal needs, a refused value), whose flag
// and control must be seen to be dealt with; a row holding a field the browser flagged invalid,
// until that field is fixed, since the browser cannot show its message inside a hidden row and Save
// did nothing; and a row its own component asked to show (goals-v3-presentation:reveal, dispatched
// from inside the row: a visit note goal the therapist added to the session or jumped to from the
// sidebar), until the choice is next changed. The choice itself stays what the user made.
// Patient::Goal::Presentation reads it the same way on the server. On the fill page the choice
// rides with the document's form as hidden fields under the field value; on the show page Save
// posts it to the url value, and closing the picker without saving puts the page back.
//
// Values:
//   saved  { active, shown, offered, offeredKnown, changed } the choice as saved, and the goals the
//          document itself changed or created, which show whatever the choice says (a signed record
//          never hides what it did to a goal): the checklist lists them checked and fixed
//   field  the form's presentation root, e.g. "care_plan[presentation]" (fill page)
//   url    the document's update_presentation path (show page)
// Targets:
//   row     a goal's row: data-gv3-goal-id, and data-gv3-parent-id on a short-term goal that is not
//           nested in its long-term goal's row; data-gv3-goal-label and data-gv3-goal-text, on the
//           row or on the elements inside it that show them
//   modal   the picker
//   list    the checklist's table body
//   note    the line saying goals not saved yet are not listed
//   changedNote  the line saying the goals the document changed or created always show
//   fields  the hidden form fields (fill page)
const ROW = '[data-goals-v3-presentation-target~="row"]';

export default class extends Controller {
  static targets = ['row', 'modal', 'list', 'note', 'changedNote', 'fields'];

  static values = { saved: Object, field: String, url: String };

  connect() {
    this.invalidRows = new Set();
    this.revealedRows = new Set();
    // invalid does not bubble: listen on the way down to the field.
    this.onInvalid = (event) => this.revealInvalid(event);
    this.element.addEventListener('invalid', this.onInvalid, true);
    // A component sharing the section asks for one of its rows with goals-v3-presentation:reveal,
    // dispatched from inside the row.
    this.onReveal = (event) => this.reveal(event);
    this.element.addEventListener('goals-v3-presentation:reveal', this.onReveal);
    this.restore();
    this.onHidden = () => {
      if (this.urlValue && !this.saving) this.restore();
    };
    this.jquery()?.on('hidden.bs.modal', this.onHidden);
  }

  disconnect() {
    this.element.removeEventListener('invalid', this.onInvalid, true);
    this.element.removeEventListener('goals-v3-presentation:reveal', this.onReveal);
    this.jquery()?.off('hidden.bs.modal', this.onHidden);
  }

  // The choice as saved, back on the page and in the form.
  restore() {
    const saved = this.savedValue || {};
    this.choice = {
      active: saved.active === true,
      shown: new Set((saved.shown || []).map(String)),
      offered: new Set((saved.offered || []).map(String)),
      offeredKnown: saved.offeredKnown === true,
    };
    this.changed = new Set((saved.changed || []).map(String));
    this.apply();
    this.writeFields();
  }

  isShown(id) {
    const { active, shown, offered, offeredKnown } = this.choice;
    if (!active || !id || this.changed.has(id)) return true;
    return shown.has(id) || (offeredKnown && !offered.has(id));
  }

  // Builds the checklist from the rows on the page now: each long-term goal with its short-term
  // goals under it, in page order.
  open() {
    if (!this.hasListTarget) return;
    const entries = this.rowTargets
      .filter((row) => row.dataset.gv3GoalId && !row.hidden)
      .map((row) => ({
        id: row.dataset.gv3GoalId,
        parent: this.parentOf(row),
        label: this.read(row, 'gv3GoalLabel', '[data-gv3-goal-label]'),
        text: this.read(row, 'gv3GoalText', '[data-gv3-goal-text]'),
      }));
    const listed = new Set(entries.map((entry) => entry.id));
    const ordered = [];
    entries.forEach((entry) => {
      if (entry.parent && listed.has(entry.parent)) return;
      ordered.push(entry);
      entries.filter((child) => child.parent === entry.id).forEach((child) => ordered.push(child));
    });
    this.listTarget.replaceChildren(...ordered.map((entry) => this.line(entry)));
    if (this.hasNoteTarget) {
      this.noteTarget.hidden = !this.rowTargets.some((row) => !row.dataset.gv3GoalId && !row.hidden);
    }
    if (this.hasChangedNoteTarget) {
      this.changedNoteTarget.hidden = !entries.some((entry) => this.changed.has(entry.id));
    }
  }

  change(event) {
    const box = event.target;
    const boxes = this.boxes();
    if (!box.checked && !box.dataset.parent) {
      // A short-term goal the document changed stays checked (its box is fixed), and keeps its
      // long-term goal's row on the page with it.
      boxes.forEach((other) => {
        if (other.dataset.parent === box.dataset.goal && !other.disabled) other.checked = false;
      });
    }
    if (box.checked && box.dataset.parent) {
      boxes.forEach((other) => {
        if (other.dataset.goal === box.dataset.parent) other.checked = true;
      });
    }
    this.choice = {
      active: true,
      shown: new Set(boxes.filter((other) => other.checked).map((other) => other.dataset.goal)),
      offered: new Set(boxes.map((other) => other.dataset.goal)),
      offeredKnown: true,
    };
    this.revealedRows.clear();
    this.apply();
    this.writeFields();
  }

  reset() {
    this.boxes().forEach((box) => {
      box.checked = true;
    });
    this.choice = { active: false, shown: new Set(), offered: new Set(), offeredKnown: true };
    this.revealedRows.clear();
    this.apply();
    this.writeFields();
  }

  // A row its component asks to show (goals-v3-presentation:reveal from inside it): it shows, with
  // its long-term goal, whatever the choice says, until the choice is next changed.
  reveal(event) {
    const row = event.target.closest ? event.target.closest(ROW) : null;
    if (!row || !this.rowTargets.includes(row)) return;
    this.revealedRows.add(row);
    this.apply();
  }

  // Show page: posts the choice to update_presentation, which saves it and reloads the document.
  save() {
    if (!this.urlValue) return;
    this.saving = true;
    const form = document.createElement('form');
    form.method = 'post';
    form.action = this.urlValue;
    form.hidden = true;
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    form.append(this.field('_method', 'patch'), ...this.choiceFields('presentation'));
    if (token) form.append(this.field('authenticity_token', token));
    document.body.append(form);
    form.submit();
  }

  // Every row as the choice says: a long-term goal's row shows while it or any of its short-term
  // goals does. A row the choice may not hide (keptShown) counts as shown. A row shown again sizes
  // its self-sizing boxes (a comment, a goal's text): the page sized them as it loaded, and a row
  // hidden then measured nothing.
  apply() {
    const rows = this.rowTargets;
    const shows = (row) => this.keptShown(row) || this.isShown(row.dataset.gv3GoalId);
    const shownAgain = [];
    rows.forEach((row) => {
      const id = row.dataset.gv3GoalId;
      const children = id ? rows.filter((other) => other !== row && this.parentOf(other) === id) : [];
      const visible = shows(row) || children.some(shows);
      if (visible && row.classList.contains('gv3-presentation-hidden')) shownAgain.push(row);
      row.classList.toggle('gv3-presentation-hidden', !visible);
    });
    if (shownAgain.length) fitAutoresizeTextareas(...shownAgain);
    this.dispatch('changed');
  }

  // A row no choice hides: one a save flagged, one its component asked to show, and one holding a
  // field the browser flagged invalid, while it still holds an invalid field.
  keptShown(row) {
    if (row.dataset.gv3Flagged === 'true') return true;
    if (this.revealedRows.has(row)) return true;
    if (!this.invalidRows.has(row)) return false;
    if (this.holdsInvalidField(row)) return true;
    this.invalidRows.delete(row);
    return false;
  }

  // The browser flagged a field invalid (on a submit, or a validity check): the row it sits in shows,
  // at once, before the browser tries to point at it.
  revealInvalid(event) {
    const row = event.target.closest ? event.target.closest(ROW) : null;
    if (!row || !this.rowTargets.includes(row)) return;
    this.invalidRows.add(row);
    this.apply();
  }

  holdsInvalidField(row) {
    return [...row.querySelectorAll('input, select, textarea')].some(
      (field) => field.willValidate && field.validity && !field.validity.valid
    );
  }

  // Fill page: the choice as the document's form submits it.
  writeFields() {
    if (!this.hasFieldsTarget || !this.fieldValue) return;
    this.fieldsTarget.replaceChildren(...this.choiceFields(this.fieldValue));
  }

  choiceFields(root) {
    const { active, shown, offered, offeredKnown } = this.choice;
    const fields = [this.field(`${root}[show_selected_goals]`, active ? '1' : '0')];
    // Present even when empty, so the server clears what an earlier choice offered.
    if (offeredKnown) fields.push(this.field(`${root}[offered_goals][]`, ''));
    if (active) {
      shown.forEach((id) => fields.push(this.field(`${root}[show_goals][${id}]`, '1')));
      if (offeredKnown) offered.forEach((id) => fields.push(this.field(`${root}[offered_goals][]`, id)));
    }
    return fields;
  }

  line(entry) {
    this.counter = (this.counter || 0) + 1;
    const boxId = `${this.hasModalTarget ? this.modalTarget.id : 'gv3-presentation'}-box-${this.counter}`;
    const row = document.createElement('tr');
    const pick = document.createElement('td');
    pick.className = 'text-nowrap';
    if (entry.parent) {
      const indent = document.createElement('i');
      indent.className = 'bi bi-arrow-return-right px-2';
      indent.setAttribute('aria-hidden', 'true');
      pick.append(indent);
    }
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.id = boxId;
    box.className = 'mt-1 mr-1';
    box.checked = this.isShown(entry.id);
    // A goal the document changed or created always shows: listed checked, and fixed.
    box.disabled = this.changed.has(entry.id);
    box.dataset.goal = entry.id;
    if (entry.parent) box.dataset.parent = entry.parent;
    box.dataset.action = 'change->goals-v3-presentation#change';
    const label = document.createElement('label');
    label.htmlFor = boxId;
    label.className = 'mb-0';
    const strong = document.createElement('strong');
    strong.textContent = entry.label;
    label.append(strong);
    pick.append(box, label);
    const text = document.createElement('td');
    text.textContent = entry.text;
    row.append(pick, text);
    return row;
  }

  boxes() {
    return this.hasListTarget ? [...this.listTarget.querySelectorAll('input[type="checkbox"][data-goal]')] : [];
  }

  // A short-term goal's long-term goal: named on the row, or the row it sits in.
  parentOf(row) {
    if (row.dataset.gv3ParentId) return row.dataset.gv3ParentId;
    const outer = row.parentElement?.closest(ROW);
    return outer?.dataset.gv3GoalId || '';
  }

  // A row's label or text: from the row itself, or from the element inside it (and not inside a
  // short-term goal's row nested in it) that shows it.
  read(row, key, selector) {
    if (row.dataset[key] !== undefined) return row.dataset[key];
    const own = [...row.querySelectorAll(selector)].find((element) => element.closest(ROW) === row);
    return own ? own.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  field(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    return input;
  }

  jquery() {
    return window.jQuery && this.hasModalTarget ? window.jQuery(this.modalTarget) : null;
  }
}
